import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Typography, Card, CardContent, Chip, Button,
  CircularProgress, Alert, Tooltip, Divider, LinearProgress,
} from '@mui/material';
import {
  Refresh, GpsFixed, Wifi, Flight, WbSunny,
  WarningAmber, CheckCircle, ErrorOutline,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import api from '../services/api';

/* ── helpers ──────────────────────────────────────────────────────────────── */
const LEVEL_CONFIG = {
  Low:      { color: '#69f0ae', bg: 'rgba(105,240,174,0.10)', icon: <CheckCircle />,    label: 'Low Activity'      },
  Moderate: { color: '#ffca28', bg: 'rgba(255,202,40,0.10)',  icon: <WarningAmber />,   label: 'Moderate Activity' },
  High:     { color: '#ef5350', bg: 'rgba(239,83,80,0.10)',   icon: <ErrorOutline />,   label: 'High Activity'     },
};

const kpColor = (kp) => {
  if (kp < 3) return '#69f0ae';
  if (kp < 5) return '#ffca28';
  if (kp < 7) return '#ffa726';
  return '#ef5350';
};

const card = {
  background: 'linear-gradient(135deg,rgba(13,13,13,0.97) 0%,rgba(8,8,8,0.93) 100%)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 3,
};

const AUTO_REFRESH_MS = 5 * 60 * 1000;

/* ── sub-components ───────────────────────────────────────────────────────── */
function ImpactCard({ icon, label, value, level }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.Low;
  return (
    <Card sx={{ ...card, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ color: cfg.color, display: 'flex' }}>{icon}</Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500, lineHeight: 1.4 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function KpChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const kp = payload[0].value;
  return (
    <Box sx={{ bgcolor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: kpColor(kp), fontWeight: 700 }}>Kp {kp}</Typography>
    </Box>
  );
}

/* ── main page ────────────────────────────────────────────────────────────── */
export default function SpaceWeatherImpactPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch]   = useState(null);

  const fetchData = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const endpoint = force ? '/space-weather/refresh' : '/space-weather';
      const res = force
        ? await api.post(endpoint)
        : await api.get(endpoint);

      setData(res.data.data);
      setLastFetch(new Date());
    } catch (e) {
      setError('Failed to load space weather impact data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(() => fetchData(), AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={48} sx={{ color: '#fff' }} />
        <Typography sx={{ color: 'text.secondary' }}>Loading real-time space weather impact...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => fetchData()}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  const cfg   = LEVEL_CONFIG[data?.level] || LEVEL_CONFIG.Low;
  const trend = data?.kpTrend || [];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

      {/* ── High activity alert banner ─────────────────────────────────── */}
      {data?.level === 'High' && (
        <Alert
          severity="error"
          icon={<WarningAmber />}
          sx={{ mb: 3, borderRadius: 2, bgcolor: 'rgba(239,83,80,0.12)', border: '1px solid rgba(239,83,80,0.4)', color: '#ef5350' }}
        >
          <strong>High Space Weather Activity Detected!</strong> GPS, satellite communications, and polar flight routes may be significantly affected.
        </Alert>
      )}

      {/* ── Page header ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>
            Space Weather Impact
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Real-time analysis of how solar activity affects GPS, internet, and aviation
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {lastFetch && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Updated {lastFetch.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Force refresh from NOAA & NASA">
            <Button
              variant="outlined"
              size="small"
              startIcon={refreshing ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <Refresh />}
              onClick={() => fetchData(true)}
              disabled={refreshing}
              sx={{ borderColor: 'rgba(255,255,255,0.25)', color: '#fff', '&:hover': { borderColor: '#fff' } }}
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Level + Kp + Flare hero row ───────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>

        {/* Current level card */}
        <Grid item xs={12} md={5}>
          <Card sx={{ ...card, border: `1px solid ${cfg.color}40`, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, mb: 2 }}>
                Current Level
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ color: cfg.color, fontSize: 40, display: 'flex' }}>
                  <WbSunny sx={{ fontSize: 48 }} />
                </Box>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: cfg.color, lineHeight: 1 }}>
                    {data?.level}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{cfg.label}</Typography>
                </Box>
              </Box>
              <Chip
                icon={React.cloneElement(cfg.icon, { style: { color: cfg.color, fontSize: 16 } })}
                label={data?.level === 'Low' ? 'All systems normal' : data?.level === 'Moderate' ? 'Minor disruptions possible' : 'Significant disruptions likely'}
                sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Kp index */}
        <Grid item xs={6} md={3.5}>
          <Card sx={{ ...card, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, mb: 2 }}>
                Kp Index
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 900, color: kpColor(data?.kpIndex || 0), lineHeight: 1, mb: 1 }}>
                {data?.kpIndex}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={((data?.kpIndex || 0) / 9) * 100}
                sx={{
                  height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)',
                  '& .MuiLinearProgress-bar': { bgcolor: kpColor(data?.kpIndex || 0), borderRadius: 3 },
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Scale 0–9 (9 = extreme storm)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Latest flare */}
        <Grid item xs={6} md={3.5}>
          <Card sx={{ ...card, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, mb: 2 }}>
                Latest Solar Flare
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900, lineHeight: 1, mb: 1,
                  color: data?.flare === 'None' ? 'text.secondary'
                       : data?.flare?.startsWith('X') ? '#ef5350'
                       : data?.flare?.startsWith('M') ? '#ffa726'
                       : '#ffca28',
                }}
              >
                {data?.flare}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {data?.flare === 'None' ? 'No recent flares detected' : 'Class detected this week'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Impact cards ─────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <ImpactCard icon={<GpsFixed />} label="GPS Impact" value={data?.gpsImpact} level={data?.level} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ImpactCard icon={<Wifi />} label="Internet / Satellite" value={data?.internetImpact} level={data?.level} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ImpactCard icon={<Flight />} label="Aviation Impact" value={data?.flightImpact} level={data?.level} />
        </Grid>
      </Grid>

      {/* ── Kp trend chart ───────────────────────────────────────────────── */}
      {trend.length > 0 && (
        <Card sx={{ ...card, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
              Kp Index — Last 24 Readings
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              Source: NOAA Space Weather Prediction Center
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="time" tick={{ fill: '#666', fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
                <YAxis domain={[0, 9]} tick={{ fill: '#666', fontSize: 11 }} tickLine={false} />
                <RTooltip content={<KpChartTooltip />} />
                <ReferenceLine y={5} stroke="#ffa726" strokeDasharray="4 4" label={{ value: 'G1 Storm', fill: '#ffa726', fontSize: 10, position: 'insideTopLeft' }} />
                <ReferenceLine y={7} stroke="#ef5350" strokeDasharray="4 4" label={{ value: 'G3 Storm', fill: '#ef5350', fontSize: 10, position: 'insideTopLeft' }} />
                <Line
                  type="monotone" dataKey="kp"
                  stroke="#fff" strokeWidth={2}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    return <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={3} fill={kpColor(payload.kp)} stroke="none" />;
                  }}
                  activeDot={{ r: 5, fill: '#fff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* ── Why this is happening ─────────────────────────────────────────── */}
      {data?.whyExplanation && (
        <Card sx={card}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1.5 }}>
              Why is this happening?
            </Typography>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.8 }}>
              {data.whyExplanation}
            </Typography>
            {data.timestamp && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block' }}>
                Data timestamp: {new Date(data.timestamp).toLocaleString()}
                {' · '}Auto-refreshes every 5 minutes
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
